'use client';

import { format } from 'date-fns';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { FaArrowTrendDown, FaArrowTrendUp } from 'react-icons/fa6';
import { cn } from '../../../../lib/utils';
import type { CryptoRate } from '../../../../types/shared';

const formatCurrency = (
  value: number | undefined | null,
  currency: 'USD' | 'RUB',
  compact = false,
) => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return value.toLocaleString(currency === 'USD' ? 'en-US' : 'ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: currency === 'RUB' ? 4 : 2,
    notation: compact ? 'compact' : undefined,
  });
};

interface CryptoDetailsHeaderProps {
  crypto: CryptoRate; // ✅ это твой DTO из types/shared (со строками)
  lastUpdated?: string | null; // ✅ ISO string
  source?: string | null;
}

export default function CryptoDetailsHeader({
  crypto,
  lastUpdated,
  source,
}: CryptoDetailsHeaderProps) {
  const { isPositiveChange, changeColor, sourceName } = useMemo(() => {
    const isPositive = crypto.price_change_percentage_24h >= 0;
    const color = isPositive ? 'text-green-400' : 'text-red-400';
    const sName =
      source === 'coincap'
        ? 'CoinCap'
        : source === 'coingecko'
          ? 'CoinGecko'
          : source === 'database_fallback'
            ? 'База данных (резерв)'
            : 'Внешний API';
    return { isPositiveChange: isPositive, changeColor: color, sourceName: sName };
  }, [crypto.price_change_percentage_24h, source]);

  const fallback = `https://placehold.co/56x56/374151/FFFFFF?text=${crypto.symbol.slice(0, 3)}`;

  const [imgSrc, setImgSrc] = useState(crypto.image || fallback);
  useEffect(() => {
    setImgSrc(crypto.image || fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crypto.image, crypto.symbol]);

  const lastUpdatedDate = useMemo(() => {
    if (!lastUpdated) return null;
    const d = new Date(lastUpdated);
    return isNaN(d.getTime()) ? null : d;
  }, [lastUpdated]);

  return (
    <header className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg p-4 md:p-6">
      <div className="flex flex-col sm:flex-row items-center sm:items-start sm:justify-between gap-4 md:gap-6">
        <div className="flex items-center gap-3 md:gap-4 w-full sm:w-auto">
          <div className="relative shrink-0">
            <Image
              src={imgSrc}
              alt={`Логотип ${crypto.name}`}
              width={56}
              height={56}
              sizes="56px"
              className="rounded-full aspect-square object-cover bg-slate-700"
              priority
              unoptimized
              onError={() => setImgSrc(fallback)}
            />
            <span className="sr-only">{crypto.name}</span>
          </div>

          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex flex-wrap items-baseline gap-2">
              {crypto.name}
              <span className="text-slate-400 text-xl font-medium uppercase">
                ({crypto.symbol})
              </span>
            </h1>

            {(lastUpdatedDate || source) && (
              <p className="text-sm text-slate-400 mt-1 flex flex-wrap gap-x-2">
                {lastUpdatedDate && (
                  <time dateTime={lastUpdatedDate.toISOString()}>
                    Обновлено: {format(lastUpdatedDate, 'dd.MM.yyyy HH:mm')}
                  </time>
                )}
                {lastUpdatedDate && source && <span aria-hidden="true">•</span>}
                {source && <span>Источник: {sourceName}</span>}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end w-full sm:w-auto mt-2 sm:mt-0 tabular-nums">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl md:text-3xl font-semibold text-white">
              {formatCurrency(crypto.current_price_usd, 'USD')}
            </span>

            <span className={cn('inline-flex items-center gap-1 text-lg', changeColor)}>
              {isPositiveChange ? (
                <FaArrowTrendUp className="h-4 w-4" />
              ) : (
                <FaArrowTrendDown className="h-4 w-4" />
              )}
              <span>{Math.abs(crypto.price_change_percentage_24h).toFixed(2)}%</span>
            </span>
          </div>

          <div className="mt-1 text-right">
            <p className="text-slate-300 text-lg">
              {formatCurrency(crypto.current_price_rub, 'RUB')}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Капитализация:{' '}
              <span className="font-medium text-white">
                {formatCurrency(crypto.market_cap_usd, 'USD', true)}
              </span>
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}
